<template>
  <div>
    <v-container
      fluid
      class="content-wrapper px-sm-4 px-xl-4 px-lg-4 px-md-10 pt-0 pt-sm-0 pt-lg-0 pt-xl-0"
    >
      <v-row>
        <v-col cols="12" md="auto">
          <v-btn
            depressed
            rounded
            small
            color="blue"
            class="white--text font-weight-bold"
            @click="goBack"
          >
            <v-icon color="black" small class="mr-2">
              mdi-arrow-left
            </v-icon>
            Back
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="mt-2">
        <v-col cols="12">
          <v-card elevation="0" class="provider-card">
            <v-row class="pa-4 pa-xs-2 pa-sm-2 pa-xl-8 pa-lg-8">
              <v-col cols="12" sm="4" lg="4" xl="4">
                <v-img
                  class="provider-image"
                  lazy-src="@/assets/img/blur.jpg"
                  :src="provider.logoUrl"
                  contain
                ></v-img>
                <div class="pt-2 pl-2" v-if="$store.getters.roles === 'super-admin'">
                  <v-icon>$vuetify.icons.eye</v-icon>
                  &nbsp;<b>{{ provider.views }} registered views</b>
                  <br />
                  <v-icon>$vuetify.icons.eye</v-icon>
                  &nbsp;<b>{{ provider.viewsNonRegistered }} non registered views</b>
                </div>
              </v-col>
              <v-col cols="12" sm="8" lg="8" xl="8">
                <div class="d-flex flex-column text-left">
                  <h3 class="primary200--text header-marketplace">{{ provider.name }}</h3>
                  <v-divider class="my-4"></v-divider>
                  <div class="label-marketplace">Description:</div>
                  <div v-if="provider.description">
                    <div v-if="!readMore">
                      {{ provider.description.slice(0, 200)
                      }}{{ provider.description.length > 200 ? '...' : '' }}
                    </div>
                    <div v-if="provider.description.length > 200">
                      <div v-if="readMore">{{ provider.description }}</div>
                    </div>
                    <div v-if="provider.description.length > 200" class="d-flex justify-end">
                      <v-btn
                        text
                        @click="readMore = !readMore"
                        class="text-decoration-underline text-bold"
                      >
                        {{ !readMore ? 'Read More' : 'Read Less' }}
                      </v-btn>
                    </div>
                  </div>
                  <v-divider class="my-4"></v-divider>
                  <div class="label-marketplace">Hosting Institution:</div>
                  <div class="neutral500--text">
                    {{ provider.hostingInstitution ? provider.hostingInstitution.name : '' }}
                  </div>
                  <v-divider class="my-4"></v-divider>
                  <div class="label-marketplace">Website</div>
                  <div>
                    <a :href="provider.website" target="_blank" class="on-hover-underline">{{
                      provider.website
                    }}</a>
                  </div>
                  <v-divider class="my-4"></v-divider>
                  <div class="label-marketplace">Social Media</div>
                  <div>
                    <social-media :list="provider.socialMedia"></social-media>
                  </div>
                  <v-divider class="my-4"></v-divider>
                  <div class="label-marketplace">Tags</div>
                  <div>
                    <v-chip
                      v-for="(tag, index) in provider.tags"
                      :key="'tag-' + provider.id + '-' + index"
                      class="mr-2"
                      :color="color(index)"
                    >
                      {{ tag }}
                    </v-chip>
                  </div>
                  <div class="my-4 text-right" v-if="provider.publicContactsVerified.length > 0">
                    <v-btn
                      v-if="!roles"
                      depressed
                      rounded
                      large
                      color="black"
                      outlined
                      class="black--text font-weight-bold"
                      data-cy="submit"
                      @click.stop="modalLogIn = true"
                    >
                      <v-icon>mdi-email-outline</v-icon>
                      Contact
                    </v-btn>
                    <v-btn
                      v-else
                      depressed
                      rounded
                      large
                      color="black"
                      outlined
                      class="black--text font-weight-bold"
                      data-cy="submit"
                      :href="'mailto:' + provider.publicContactsVerified[0].email"
                    >
                      <v-icon>mdi-email-outline</v-icon>
                      Contact
                    </v-btn>
                  </div>
                </div>
              </v-col>
              <v-col cols="12">
                <v-tabs v-model="tab" show-arrows active-class="neutral800--text">
                  <v-tabs-slider color="neutral800"></v-tabs-slider>
                  <v-tab>{{ 'Services' }}</v-tab>
                  <v-tab>{{ $t('classification') }}</v-tab>
                  <v-tab>{{ $t('contact') }}</v-tab>
                  <v-tab>{{ $t('location') }}</v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab">
                  <v-tab-item
                    :transition="false"
                    :reverse-transition="false"
                    :key="0"
                    class="px-0 py-0 px-sm-4 py-sm-6 px-lg-4 py-lg-6 px-xl-4 py-xl-6 text-left"
                  >
                    <v-row>
                      <v-col cols="12" class="text-right">
                        <router-link
                          v-if="!$router.currentRoute.meta.requiresAuth && viewMore"
                          class="primary200--text"
                          :to="{
                            name: 'PublicMarketplaceServices',
                            query: { providers: this.provider.id },
                          }"
                        >
                          {{ $t('view-more') }}
                        </router-link>
                        <router-link
                          v-else-if="viewMore"
                          class="primary200--text"
                          :to="{
                            name: 'MarketplaceServices',
                            query: { providers: this.provider.id },
                          }"
                        >
                          {{ $t('view-more') }}
                        </router-link>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                        v-for="resource in resources"
                        :key="'resource-' + resource.id"
                        class="mb-1 d-flex"
                        style="flex-direction:column"
                      >
                        <v-hover v-slot="{ hover }">
                          <v-card
                            :elevation="hover ? 4 : 0"
                            :class="{ 'on-hover': hover }"
                            class="resource-card flex-grow-1"
                            @click="onResourceClick(resource)"
                          >
                            <v-row class="pa-xl-8 pa-lg-8 pa-sm-8 pa-2">
                              <v-col cols="12" xl="3" sm="3" lg="3">
                                <v-img
                                  max-height="159"
                                  lazy-src="@/assets/img/blur.jpg"
                                  class="resource-image"
                                  :src="resource.logoUrlPublic"
                                  contain
                                ></v-img>
                              </v-col>
                              <v-col cols="12" xl="9" sm="9" lg="9">
                                <div class="d-flex flex-column">
                                  <div>
                                    <h3 class="mb-1 neutral800--text">{{ resource.name }}</h3>
                                    <div class="mb-1">
                                      {{ resource.provider.name }}
                                    </div>
                                  </div>
                                  <div class="mt-2">
                                    <v-chip
                                      v-for="(tag, index) in resource.tags"
                                      :key="'tag-' + resource.id + '-' + index"
                                      class="mr-2"
                                      :color="color(index)"
                                    >
                                      {{ tag }}
                                    </v-chip>
                                  </div>
                                </div>
                              </v-col>
                            </v-row>
                          </v-card>
                        </v-hover>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item
                    :transition="false"
                    :reverse-transition="false"
                    :key="1"
                    class="px-4 py-6 text-left"
                  >
                    <div class="label-marketplace">Scientific Domain / Subdomain:</div>
                    <div
                      v-for="(scientific, index) in provider.scientifics"
                      :key="'scientific-' + provider.id + '-' + index"
                    >
                      {{ scientific.domain }} / {{ scientific.subDomain }}
                    </div>
                    <div class="label-marketplace mt-6">{{ $t('structure-type') }}</div>
                    <div v-if="provider.structureTypes">
                      {{ provider.structureTypes.map(item => item.name).join(', ') }}
                    </div>
                  </v-tab-item>
                  <v-tab-item
                    :transition="false"
                    :reverse-transition="false"
                    :key="2"
                    class="px-4 py-6 text-left"
                  >
                    <div
                      v-for="(contact, index) in provider.publicContacts"
                      :key="'pubic-contact-' + provider.id + '-' + index"
                      class="d-flex flex-column mb-6"
                    >
                      <div class="pb-2">
                        <span class="label-marketplace">First Name: </span> {{ contact.firstname }}
                      </div>
                      <div class="pb-2">
                        <span class="label-marketplace">Last Name: </span> {{ contact.lastname }}
                      </div>
                      <div class="pb-2" v-if="contact.position">
                        <span class="label-marketplace">Position: </span>
                        {{ contact.position }}
                      </div>
                      <div class="pb-2" v-if="contact.phone">
                        <span class="label-marketplace">Phone: </span>
                        {{ contact.phone }}
                      </div>
                      <div class="pb-2">
                        <span class="label-marketplace">Email: </span> {{ contact.email }}
                      </div>
                    </div>
                  </v-tab-item>
                  <v-tab-item
                    :transition="false"
                    :reverse-transition="false"
                    :key="3"
                    class="px-4 py-6 text-left"
                  >
                    <v-row>
                      <v-col cols="12" md="6">
                        <div class="label-marketplace">{{ $t('city') }}</div>
                        <div>
                          {{ provider.city }}
                        </div>
                        <div class="label-marketplace mt-6">{{ $t('street-name-number') }}</div>
                        <div>
                          {{ provider.street }}
                        </div>
                        <div class="label-marketplace mt-6">{{ $t('po-box') }}</div>
                        <div>
                          {{ provider.poBox }}
                        </div>
                        <div class="label-marketplace mt-6">{{ $t('country') }}</div>
                        <div>
                          {{ provider.country.description }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="6" v-if="markerLatLng != null">
                        <div>
                          <l-map
                            style="height: 400px"
                            :zoom="zoom"
                            :center="markerLatLng"
                            :options="{ attributionControl: false }"
                          >
                            <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
                            <l-marker :lat-lng="markerLatLng"></l-marker>
                            <l-control-attribution
                              position="bottomright"
                              prefix=""
                            ></l-control-attribution>
                          </l-map>
                        </div>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-dialog v-model="modalLogIn" max-width="1000px">
        <v-card
          class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8"
        >
          <v-card-title class="px-0 py-0">
            <v-row class="mx-0 px-2" align="center">
              <v-col cols="11" class="text-center d-flex justify-center align-center">
                This action requires authentication
              </v-col>
              <v-col cols="1" class="pl-0">
                <v-btn fab small text @click="modalLogIn = false">
                  <v-icon color="neutral500">
                    mdi-close
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-divider class="mt-1 mb-6 my-sm-6 my-lg-6 my-xl-6 my-md-6"></v-divider>
          <v-card-text class="pa-0">
            Only authenticated users are allowed to perform this action. Please
            <router-link :to="{ name: 'MarketplaceProvider', params: { id: provider.id } }"
              >Log In</router-link
            >
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus';
import SocialMedia from '@/components/marketplace/SocialMedia';
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LControlAttribution } from 'vue2-leaflet';
import util from '@/mixins/util.js';

export default {
  name: 'MarketplaceProvider',
  mixins: [util],
  components: { SocialMedia, LMap, LTileLayer, LMarker, LControlAttribution },
  data() {
    return {
      breadcrumbs: this.$router.currentRoute.meta.breadcrumbPath,
      provider: {
        hostingInstitution: {},
        scientifics: [],
        categories: [],
        publicContacts: [],
        mainContact: {},
        country: {},
        coordinates: null,
        publicContactsVerified: [],
      },
      tab: null,
      isEquipment: false,
      resources: [],
      viewMore: false,
      zoom: 13,
      url: process.env.VUE_APP_MAPTILE_PREFIX + '/{z}/{x}/{y}.png',
      attribution:
        '<a href="https://leafletjs.com/">Leaflet</a> | &copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      readMore: false,
      roles: null,
      modalLogIn: false,
      ordersQuery: '',
      ordersServicesQuery: '',
      marketplaceEquipmentViewAll: '',
      marketplaceServicesViewAll: '',
    };
  },
  created() {
    this.providerId = sessionStorage.getItem('providerId');
    this.roles = localStorage.getItem('roles');
  },
  mounted() {
    let id = this.$route.params.id;
    if (id != null) {
      EventBus.$emit('waiting', true);
      this.axios
        .get('/api/v1/providers/' + id)
        .then(res => {
          this.provider = res.data;
          this.$set(this.breadcrumbs, this.breadcrumbs.length - 1, { text: this.provider.name });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
      this.axios
        .get('/api/v1/resources?providers=' + id)
        .then(res => {
          this.resources = res.data.content;
          this.viewMore = res.data.total > res.data.contentSize;
        })
        .catch(error => {
          console.log({ error });
        });
    }
    this.ordersQuery = this.$route.query.ordersQuery;
    this.ordersServicesQuery = this.$route.query.ordersServicesQuery;
    this.marketplaceEquipmentViewAll = this.$route.query.marketplaceEquipmentViewAll;
    this.marketplaceServicesViewAll = this.$route.query.marketplaceServicesViewAll;
  },
  methods: {
    onResourceClick(item) {
      if (!this.$router.currentRoute.meta.requiresAuth) {
        this.$router.push({
          name: 'PublicMarketplaceService',
          params: { id: item.id },
        });
      } else {
        this.$router.push({
          name: 'MarketplaceService',
          params: { id: item.id },
        });
      }
    },
    goBack() {
      if (this.marketplaceEquipmentViewAll && !this.$store.getters.roles) {
        const marketplaceEquipmentViewAll = true;
        const selectedCatalogue = 'Browse the Catalogue of Equipment';
        let routePath = '';
        routePath = '/public-catalogue-marketplace/equipment';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceEquipmentViewAll },
        });
      } else if (this.marketplaceEquipmentViewAll && this.$store.getters.roles) {
        const marketplaceEquipmentViewAll = true;
        const selectedCatalogue = 'Browse the Catalogue of Equipment';
        let routePath = '';
        routePath = '/marketplace/equipment';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceEquipmentViewAll },
        });
      } else if (this.marketplaceServicesViewAll && !this.$store.getters.roles) {
        const marketplaceServicesViewAll = true;
        const selectedCatalogue = 'Browse the Catalogue of Services';
        let routePath = '';
        routePath = '/public-catalogue-marketplace/services';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceServicesViewAll },
        });
      } else if (this.marketplaceServicesViewAll && this.$store.getters.roles) {
        const marketplaceServicesViewAll = true;
        const selectedCatalogue = 'Browse the Catalogue of Services';
        let routePath = '';
        routePath = '/marketplace/services';
        this.$router.push({
          path: routePath,
          query: { selectedCatalogue, marketplaceServicesViewAll },
        });
      } else if (this.ordersQuery) {
        this.$router.push({
          name: 'MyOrders',
          query: { toggleOrder: 'equipment' },
        });
      } else if (this.ordersServicesQuery) {
        this.$router.push({
          name: 'MyOrders',
          query: { toggleOrder: 'services' },
        });
      } else {
        this.$router.go(-1);
      }
    },
  },
  computed: {
    markerLatLng() {
      if (this.provider == null || this.provider.coordinates == null) {
        return null;
      } else {
        const coordinates = this.provider.coordinates.split(',');
        try {
          return L.latLng(coordinates[0].trim(), coordinates[1].trim());
        } catch (e) {
          console.log(e);
          return null;
        }
      }
    },
  },
};
</script>

<style scoped>
.resource-card {
  border: 1px solid #f2f2f2;
  border-radius: 6px;
  box-shadow: 1px 4px 43px -9px rgba(124, 122, 144, 0.15);
}

.provider-image {
  border-radius: 19.875px;
}
</style>
